import GerberFileIdentifier, {
  GERBER_FILE_LAYER_MAPPING_SCHEMA,
} from "../../../UploadBoard/GerberFileIdentifier";
import { Formik } from "formik";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import {
  Board,
  GerberFileLayerMapping,
  updateGerberLayerMapping,
} from "../../../../reducers/uploadedBoard";
import { useCallback, useEffect, useState } from "react";
import useFetchAndTrace from "../../../../utils/hooks/fetchWithTrace";
import { useCustomAlert } from "../../../../utils/hooks/alerts";
import LoadingIcon from "../../../common/LoadingIcon";
import * as Yup from "yup";
import Button from "../../../common/Button";
import { ButtonTypes } from "../../../common/Button/constants";
import { EditMappingForm } from "./BoardPreviewStyles";
import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import LoadingOverlay from "../../../LoadingOverlay";
import useBoardFiles from "../../../../utils/hooks/commonly-stored/uploaded-board/files";
import { useAppDispatch } from "../../../../utils/hooks/store";

function useDownloadFileFromS3AsBlob(gerberZipUrl: string | null) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const customAlert = useCustomAlert();
  const fetchWithTrace = useFetchAndTrace();

  useEffect(() => {
    if (gerberZipUrl === null) {
      setBlob(null);
    }
  }, [gerberZipUrl]);

  const handleLoadFile = useCallback(async (response: Response) => {
    if (response.ok) {
      setBlob(await response.blob());
    } else {
      throw Error("Failed to retrieve blob");
    }
  }, []);

  const handleError = useCallback(() => {
    customAlert("Failed to load file options.");
  }, [customAlert]);

  useEffect(() => {
    if (blob === null && gerberZipUrl !== null) {
      void fetchWithTrace(
        gerberZipUrl,
        handleLoadFile,
        handleError,
        undefined,
        true,
      );
    }
  }, [blob, fetchWithTrace, gerberZipUrl, handleError, handleLoadFile]);

  return {
    blob,
  };
}

export default function EditFileMapping({
  onUpdate,
}: {
  onUpdate: () => void;
}) {
  const dispatch = useAppDispatch();
  const boardStuff = useBoardFromContext();
  const board = boardStuff.board as Board;
  const { boardFiles, refreshBoardFiles } = useBoardFiles(board.id);
  const { blob: gerberZip } = useDownloadFileFromS3AsBlob(
    boardFiles?.latest_unprocessed_gerber || null,
  );
  const customAlert = useCustomAlert();
  const errorHandler = () => {
    customAlert("Failed to update file mapping");
  };
  const { sendBoardEvent, loading: sendingBoardEvent } = useSendBoardEvent(
    errorHandler,
    refreshBoardFiles,
  );

  if (gerberZip === null) {
    return <LoadingIcon text={"Loading file options..."} centerHorizontally />;
  }

  const onSubmit = async (values: {
    gerberFileLayerMapping: GerberFileLayerMapping;
    bomFileInfo: null;
    pnpFileInfo: null;
  }) => {
    await sendBoardEvent(board.id, {
      machine_group: MachineGroup.BOARD,
      model_id: board.id,
      event: BoardTransitionEvent.SHOULD_UPDATE_FILE_MAPPING,
      update: { package_mapping_update: values.gerberFileLayerMapping },
    });
    dispatch(
      updateGerberLayerMapping({
        boardId: board.id,
        gerberFileLayerMapping: values.gerberFileLayerMapping,
      }),
    );
    onUpdate();
  };

  return (
    <>
      <LoadingOverlay loading={sendingBoardEvent} text={"Updating layers"} />
      <Formik
        initialValues={{
          gerberFileLayerMapping: board.upload_config
            .gerber_file_layer_mapping as GerberFileLayerMapping,
          bomFileInfo: null,
          pnpFileInfo: null,
        }}
        onSubmit={onSubmit}
        validationSchema={Yup.object().shape({
          gerberFileLayerMapping: GERBER_FILE_LAYER_MAPPING_SCHEMA,
        })}
      >
        {({ handleSubmit, isValid }) => {
          return (
            <EditMappingForm onSubmit={handleSubmit}>
              <GerberFileIdentifier
                file={gerberZip}
                gerbersOnly={true}
                mappingSuggestionsDisabled={true}
              />
              <Button
                label={"Update layer mappings"}
                type={ButtonTypes.SUBMIT}
                disabled={!isValid}
              />
            </EditMappingForm>
          );
        }}
      </Formik>
    </>
  );
}
